import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

import { useAuth } from './AuthContext';
import { DEFAULT_THEME_ID, FREE_THEME_IDS, ensureThemeFont, polarityOf } from '../themes';
import { API_URL } from '../utils/api';

const SettingsContext = createContext(null);

const STORAGE_KEY = 'cs2_settings';

// Sätts när enhetens temaval en gång har erbjudits kontot. Se adoptTheme().
const MIGRATED_KEY = 'cs2_theme_migrated';

const DEFAULT_SETTINGS = {
  // Sound
  matchFoundSound: 'match_confirm',
  soundVolume: 20,

  // Notification toggles
  notify_match_ended: true,
  notify_veto_started: true,
  notify_match_created: true,
  notify_match_launched: true,
  notify_match_cancelled: true,

  // Browser push notifications (when tab is not focused)
  browser_notifications: false,

  // Theme
  theme: DEFAULT_THEME_ID,
  // Speglar temats polaritet så inline-skriptet i index.html kan sätta
  // data-polarity före första målningen — manifestet finns inte i scope då.
  themePolarity: 'dark',
};

function loadSettings() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.warn('Failed to load settings:', e);
  }
  return { ...DEFAULT_SETTINGS };
}

/**
 * Sätter temat på <html>.
 *
 * Två attribut, inte ett. `data-theme` väljer palettblocket; `data-polarity`
 * styr reglerna som beror på om bakgrunden är ljus eller mörk — det mörka
 * chippet bakom flair, vända kartscrims. De kan inte slås ihop: Office,
 * Mushroom Kingdom och Lemmings är ljusa teman som inte heter "light", och
 * hade annars missat varenda sådan regel.
 *
 * Polariteten speglas också till localStorage av updateSetting, så
 * inline-skriptet i index.html kan sätta båda attributen före första
 * målningen utan att ha manifestet laddat.
 *
 * @param {string} theme - tema-id ur themes/index.js
 */
function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.setAttribute('data-polarity', polarityOf(theme));
  ensureThemeFont(theme);
}

/**
 * Provides user settings (sound, notifications, theme) to the component tree.
 * Settings are persisted in localStorage under the 'cs2_settings' key.
 */
export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(loadSettings);
  const { user, token } = useAuth();

  /* Temalabbets granskningsläge: ett tema påtvingat sajten utan att sparas.
   *
   * Medvetet BARA i minnet — inte i localStorage, inte på kontot. En omladdning
   * avslutar det. Det är rätt beteende för ett QA-läge: granskningen ska inte
   * kunna bli kvar av misstag och se ut som ett trasigt temaval.
   *
   * Det här är inte en väg runt betalväggen. Alla temans CSS ligger i varje
   * bundle — vem som helst kan sätta data-theme i devtools. Det man betalar
   * för är att valet SPARAS: följer kontot, överlever omladdning, finns på
   * alla enheter. Den gränsen vaktas serverside av resolve_theme(). */
  const [previewThemeId, setPreviewThemeId] = useState(null);

  // Vilka teman spelaren får välja. Servern är facit — utan den vore låset i
  // väljaren bara en klass och betalväggen kosmetisk. Utloggad: gratistemana.
  const ownedThemes = user?.owned_themes ?? FREE_THEME_IDS;

  // Följer spelaren communityns standardtema? Går INTE att utläsa ur
  // user.theme — servern resolvar tomt till det faktiska temat innan det
  // skickas, så klienten ser alltid ett konkret id. Utan den här flaggan
  // hade väljaren inte kunnat visa vilket val som är aktivt.
  const followsCommunity = user?.theme_follows_community ?? false;
  const communityTheme = user?.community_default_theme ?? DEFAULT_THEME_ID;

  // Senaste temat vi sett FRÅN servern. Behövs för att skilja "servern har ett
  // nytt värde" (byte på en annan enhet → applicera) från "samma user-objekt
  // hämtades om" (→ rör inte det optimistiska värdet vi just satt lokalt).
  const lastServerTheme = useRef(null);
  const wasLoggedIn = useRef(false);

  // Apply theme on mount and change. Granskningsläget vinner medan det är
  // aktivt; när det släpps faller sajten tillbaka på spelarens eget tema.
  const activeTheme = previewThemeId ?? settings.theme;
  useEffect(() => {
    applyTheme(activeTheme);
  }, [activeTheme]);

  /**
   * Skriver temat till localStorage-cachen och applicerar det.
   *
   * Cachen är inte lagring utan paint-time-cache: den finns för att
   * inline-skriptet i index.html ska kunna sätta attributen före första
   * målningen. Facit är serverns värde.
   *
   * @param {string} themeId - tema-id ur manifestet
   */
  const cacheTheme = useCallback((themeId) => {
    setSettings(prev => {
      const next = { ...prev, theme: themeId, themePolarity: polarityOf(themeId) };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  /**
   * Byt tema: applicera direkt och spara till kontot i bakgrunden.
   *
   * Optimistiskt med flit — att vänta på servern innan färgen byts gör bytet
   * trögt för ingenting. Misslyckas skrivningen loggas det; nästa
   * ``/api/auth/me`` rättar värdet.
   *
   * @param {string} themeId - tema-id ur manifestet
   */
  const setTheme = useCallback((themeId) => {
    // "" = följ communityn. Servern sparar tomt, men lokalt måste vi rendera
    // något: cacha communityns tema, annars blir data-theme="" tills nästa
    // /api/auth/me svarar.
    cacheTheme(themeId || communityTheme);
    if (!token) return;
    lastServerTheme.current = themeId || communityTheme;
    fetch(`${API_URL}/api/auth/me/theme`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ theme: themeId }),
    }).catch(err => console.warn('Kunde inte spara temat:', err));
  }, [cacheTheme, token, communityTheme]);

  // Synka mot kontot. Temat följer kontot, inte enheten, så samma användare
  // ser samma sajt på jobbdatorn och i mobilen.
  useEffect(() => {
    if (!user) {
      if (wasLoggedIn.current) {
        // Utloggning nollställer. Annars ärver nästa person som loggar in på
        // en delad dator föregående användares tema — och om det var ett köpt
        // tema ser sajten ut som något de inte äger.
        wasLoggedIn.current = false;
        lastServerTheme.current = null;
        cacheTheme(DEFAULT_THEME_ID);
      }
      return;
    }

    wasLoggedIn.current = true;
    const serverTheme = user.theme;

    // Engångsövergång: temat bodde bara i localStorage innan det här, så den
    // som valt ljust har ett val servern aldrig hört talas om. Utan det här
    // skrivs deras val över med kontots orörda 'dark' vid första
    // sidladdningen efter deploy — en tyst regression för alla som faktiskt
    // använder ljusa temat.
    //
    // Bara en gång per enhet, och bara när kontot står kvar på standardtemat.
    // Sedan vinner servern alltid; annars hade en gammal cache kunnat trycka
    // tillbaka ett tema användaren nyss bytt från på en annan enhet.
    if (!localStorage.getItem(MIGRATED_KEY)) {
      localStorage.setItem(MIGRATED_KEY, '1');
      // Läses ur cachen och inte ur settings-state, så effekten slipper bero
      // på temat den själv ändrar.
      const cached = loadSettings().theme;
      const owned = user.owned_themes ?? FREE_THEME_IDS;
      if (
        serverTheme === DEFAULT_THEME_ID
        && cached !== DEFAULT_THEME_ID
        && owned.includes(cached)
      ) {
        setTheme(cached);
        return;
      }
    }

    if (serverTheme && serverTheme !== lastServerTheme.current) {
      lastServerTheme.current = serverTheme;
      cacheTheme(serverTheme);
    }
  }, [user, cacheTheme, setTheme]);

  const updateSetting = useCallback((key, value) => {
    if (key === 'theme') {
      setTheme(value);
      return;
    }
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, [setTheme]);

  const shouldNotify = useCallback((eventType) => {
    const key = `notify_${eventType}`;
    return settings[key] !== false;
  }, [settings]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        updateSetting,
        shouldNotify,
        ownedThemes,
        followsCommunity,
        communityTheme,
        previewThemeId,
        setPreviewThemeId,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
